import {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
  memo,
  type RefObject,
} from "react";
import { createPortal } from "react-dom";
import {
  ReactFlow,
  Background,
  ConnectionLineType,
  Controls,
  Panel,
  MiniMap,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  type Edge,
  type Node,
  type NodeChange,
  useReactFlow,
  useStoreApi,
  getNodesBounds,
  type NodeTypes,
  MarkerType,
} from "@xyflow/react";
// Styles are provided via styles.css / styles-core.css entry points.
// Do NOT import CSS here — it breaks SSR (Node cannot load .css files).
import {
  ExternalLink,
  HistoryIcon,
  CheckIcon,
  ClipboardIcon,
  MoreVertical,
  Zap,
  Bot,
  Wrench,
  ServerIcon,
  Workflow,
  MessageSquare,
  Search as SearchIcon,
  ArrowLeftRight,
  Group as GroupIcon,
  Globe,
  User,
  Database,
  Boxes,
  Box,
  type LucideIcon,
} from "lucide-react";
import * as DropdownMenu from "@radix-ui/react-dropdown-menu";
import { toPng } from "html-to-image";
// Nodes and edges
// Studio-2 nodes (named exports from directories)
import { Service as ServiceNode } from "../nodes/service";
import AgentNode from "../nodes/Agent";
import AgentToolNode from "../nodes/AgentTool";
import { Event as EventNode } from "../nodes/event";
import { Query as QueryNode } from "../nodes/query";
import { Command as CommandNode } from "../nodes/command";
import { Channel as ChannelNode } from "../nodes/channel";
import { Data as DataNode } from "../nodes/data";
import { View as ViewNode } from "../nodes/view";
import { Actor as ActorNode } from "../nodes/actor";
import ContextActorNode from "../nodes/ContextActor";
import SystemGroupNode from "../nodes/SystemGroupNode";
import { ExternalSystem as ExternalSystemNode } from "../nodes/external-system";
import { Note as NoteNode } from "../nodes/note";
import { Field as FieldNode } from "../nodes/field";
// Core nodes (default exports from flat files)
import FlowNode from "../nodes/Flow";
import FlowExpandedNode from "../nodes/FlowExpandedNode";
import EntityNode from "../nodes/Entity";
import UserNode from "../nodes/User";
import StepNode from "../nodes/Step";
import DomainNode from "../nodes/Domain";
import SystemNode from "../nodes/System";
import GroupNode from "../nodes/GroupNode";
import CustomNode from "../nodes/Custom";
import ExternalSystemNode2 from "../nodes/ExternalSystem2";
import DataProductNode from "../nodes/DataProduct";
import {
  MessageGroupNode,
  MessageGroupExpandedNode,
} from "../nodes/message-group";
// Edges
import AnimatedMessageEdge from "../edges/AnimatedMessageEdge";
import MultilineEdgeLabel from "../edges/MultilineEdgeLabel";
import FlowEdge from "../edges/FlowEdge";
import {
  LabelledDefaultEdge,
  LabelledSmoothStepEdge,
  LabelledStepEdge,
} from "../edges/LabelledEdge";
import VisualiserSearch, { type VisualiserSearchRef } from "./VisualiserSearch";
import StepWalkthrough from "./StepWalkthrough";
import StudioModal from "./StudioModal";
import FocusModeModal from "./FocusModeModal";
import MermaidView from "./MermaidView";
import { useChannelVisibility } from "../hooks/use-channel-visibility";
import VisualizerDropdownContent from "./VisualizerDropdownContent";
import NodeContextMenu from "./NodeContextMenu";
import { convertToMermaid } from "../utils/export-mermaid";
import {
  getExportImageDimensions,
  injectExportStyles,
} from "../utils/export-image";
import { copyToClipboard } from "../utils/clipboard";
import { layoutGraph } from "../utils/layout";
import { packNodesAroundBounds } from "../utils/local-packing";
import {
  buildMessageGroupExpansionNodes,
  getExpandedMessageGroupNode,
} from "../utils/message-group-expansion";
import { AllNotesModal, getNotesFromNode } from "./NotesToolbarButton";
import { setBuildUrlFn } from "../utils/url-builder";
import { layoutDagreGraph } from "../utils/utils/utils";
import { PortalContainerProvider } from "../context/PortalContainerContext";
import type { DslGraph } from "../types";
import dagre from "dagre";

// Minimum pixel change to detect layout modifications (avoids floating point comparison issues)
const POSITION_CHANGE_THRESHOLD = 1;

// Above this node count, auto-disable message animation when the user has no stored preference
const LARGE_GRAPH_NODE_THRESHOLD = 30;

// Static props for ReactFlow - defined outside component to avoid new references on every render
const NODE_ORIGIN: [number, number] = [0.1, 0.1];
const INITIAL_FIT_VIEW_OPTIONS = { padding: 0.2, duration: 0 } as const;
const MINIMAP_STYLE = {
  backgroundColor: "rgb(var(--ec-page-bg))",
  border: "1px solid rgb(var(--ec-page-border))",
  borderRadius: "8px",
} as const;
const LAYOUT_CHANGE_PANEL_STYLE_WITH_WALKTHROUGH = {
  marginBottom: "20px",
  marginLeft: "420px",
} as const;
const LAYOUT_CHANGE_PANEL_STYLE_DEFAULT = { marginLeft: "60px" } as const;
const LEGEND_PANEL_STYLE_WITH_MINIMAP = { marginRight: "230px" } as const;

// Expanded wrapper containers are structural only — they host grouped/inlined
// child nodes and never represent a business step themselves. Centralised so
// the walkthrough, click handler, and collapse handler agree on the set.
const EXPANDED_WRAPPER_TYPES = new Set([
  "flowExpanded",
  "messageGroupExpanded",
]);
const isExpandedWrapper = (type: string | undefined) =>
  type != null && EXPANDED_WRAPPER_TYPES.has(type);

type LegendEntry = { count: number; colorClass: string; groupId?: string };

// Friendly labels for legend keys that differ from their raw node type.
const LEGEND_LABELS: Record<string, string> = {
  "context-actor": "Actors",
};

const getLegendLabel = (key: string) => LEGEND_LABELS[key] ?? key;

// Icon + text colour per legend key, mirroring each collection's node styling.
// When a key has no icon mapped, the legend falls back to its coloured square.
const LEGEND_ICONS: Record<string, { Icon: LucideIcon; colorClass: string }> = {
  events: { Icon: Zap, colorClass: "text-orange-600" },
  agent: { Icon: Bot, colorClass: "text-sky-600" },
  agents: { Icon: Bot, colorClass: "text-sky-600" },
  agentTool: { Icon: Wrench, colorClass: "text-violet-600" },
  "agent-tool": { Icon: Wrench, colorClass: "text-violet-600" },
  services: { Icon: ServerIcon, colorClass: "text-pink-600" },
  flows: { Icon: Workflow, colorClass: "text-teal-600" },
  commands: { Icon: MessageSquare, colorClass: "text-blue-600" },
  queries: { Icon: SearchIcon, colorClass: "text-green-600" },
  channels: { Icon: ArrowLeftRight, colorClass: "text-gray-600" },
  externalSystem: { Icon: Globe, colorClass: "text-pink-600" },
  systems: { Icon: GroupIcon, colorClass: "text-purple-600" },
  system: { Icon: GroupIcon, colorClass: "text-purple-600" },
  actor: { Icon: User, colorClass: "text-yellow-500" },
  "context-actor": { Icon: User, colorClass: "text-yellow-500" },
  data: { Icon: Database, colorClass: "text-blue-600" },
  "data-products": { Icon: Boxes, colorClass: "text-indigo-600" },
  field: { Icon: Box, colorClass: "text-cyan-600" },
};

const getLegendIcon = (key: string) => LEGEND_ICONS[key];

const LegendPanel = memo(function LegendPanel({
  legend,
  showMinimap,
  onLegendClick,
}: {
  legend: Record<string, LegendEntry>;
  showMinimap: boolean;
  onLegendClick: (key: string, groupId?: string) => void;
}) {
  return (
    <Panel
      position="bottom-right"
      style={showMinimap ? LEGEND_PANEL_STYLE_WITH_MINIMAP : undefined}
    >
      <div className="bg-[rgb(var(--ec-card-bg))] border border-[rgb(var(--ec-page-border))] font-light px-4 text-[12px] shadow-md py-1 rounded-md">
        <ul className="m-0 p-0 ">
          {Object.entries(legend).map(
            ([key, { count, colorClass, groupId }]) => {
              const legendIcon = getLegendIcon(key);
              return (
                <li
                  key={key}
                  className="flex space-x-2 items-center text-[10px] cursor-pointer text-[rgb(var(--ec-page-text))] hover:text-[rgb(var(--ec-accent))] hover:underline"
                  onClick={() => onLegendClick(key, groupId)}
                >
                  {legendIcon ? (
                    <legendIcon.Icon
                      className={`w-3 h-3 shrink-0 ${legendIcon.colorClass}`}
                      strokeWidth={2}
                      aria-hidden="true"
                    />
                  ) : (
                    <span className={`w-2 h-2 block ${colorClass}`} />
                  )}
                  <span className="block capitalize">
                    {getLegendLabel(key)} ({count})
                  </span>
                </li>
              );
            },
          )}
        </ul>
      </div>
    </Panel>
  );
});

interface Props {
  nodes: any;
  edges: any;
  title?: string;
  subtitle?: string;
  includeBackground?: boolean;
  includeControls?: boolean;
  linkTo?: "docs" | "visualiser";
  includeKey?: boolean;
  linksToVisualiser?: boolean;
  links?: { label: string; url: string }[];
  mode?: "full" | "simple";
  showFlowWalkthrough?: boolean;
  showSearch?: boolean;
  zoomOnScroll?: boolean;
  designId?: string;
  isStudioModalOpen?: boolean;
  setIsStudioModalOpen?: (isOpen: boolean) => void;
  isChatEnabled?: boolean;
  maxTextSize?: number;
  isDevMode?: boolean;
  resourceKey?: string;
  /** Controls whether message flow animation is enabled. When set, overrides URL params and localStorage. */
  animated?: boolean;
  /**
   * When true, message-flow animation is unsupported for this graph: it is forced
   * off (ignoring the `animated` prop, URL params and localStorage) and the
   * "Simulate Messages" toggle is hidden. Used by graphs that aren't message
   * flows, e.g. the System Diagram.
   */
  disableMessageAnimation?: boolean;
  /** When set, the graph will zoom to this node id. */
  focusNodeId?: string;
  /** Optional token to force repeated focus for the same node id. */
  focusRequestId?: number;
  /** Optional token to trigger fit-to-screen behavior. */
  fitRequestId?: number;

  // Callback API for framework integration
  /** Called when a node is clicked */
  onNodeClick?: (node: Node) => void;
  /** Called to build URLs for navigation (used in links dropdown) */
  onBuildUrl?: (path: string) => string;
  /** Called when navigation should occur */
  onNavigate?: (url: string) => void;
  /** Called to save layout positions (dev mode only) */
  onSaveLayout?: (
    resourceKey: string,
    positions: Record<string, { x: number; y: number }>,
  ) => Promise<boolean>;
  /** Called to reset layout positions (dev mode only) */
  onResetLayout?: (resourceKey: string) => Promise<boolean>;
}

// --- Shared edge style for group expand edges ---
const GROUP_EDGE_STYLE = {
  strokeWidth: 1,
  stroke: "var(--ec-edge-stroke, #6b7280)",
};
const GROUP_EDGE_MARKER = {
  type: MarkerType.ArrowClosed,
  width: 20,
  height: 20,
};

/** Label for the edge connecting the service to a message, based on direction and collection type. */
const getChildEdgeLabel = (
  direction: "sends" | "receives",
  collection: string,
) => {
  if (direction === "sends") {
    if (collection === "commands") return "invokes";
    if (collection === "queries") return "requests";
    return "publishes \nevent";
  }
  if (collection === "commands" || collection === "queries") return "accepts";
  return "subscribed by";
};

/**
 * Check if a message can reach the service through downstream edges (i.e. via channels).
 * Uses BFS over the pre-computed edge graph. If reachable, no direct child→service edge is needed.
 */
const canReachService = (
  msgId: string,
  serviceNodeId: string,
  expandedEdges: any[],
): boolean => {
  // Build adjacency list once for efficient traversal
  const adj = new Map<string, string[]>();
  for (const e of expandedEdges) {
    if (!adj.has(e.source)) adj.set(e.source, []);
    adj.get(e.source)!.push(e.target);
  }
  const visited = new Set<string>([msgId]);
  const queue = [msgId];
  while (queue.length > 0) {
    const current = queue.shift()!;
    for (const next of adj.get(current) || []) {
      if (next === serviceNodeId) return true;
      if (!visited.has(next)) {
        visited.add(next);
        queue.push(next);
      }
    }
  }
  return false;
};

/**
 * Build edges connecting the service to child message nodes inside an expanded group.
 * - For sends: always creates service → child edge.
 * - For receives: only creates child → service edge when no channel path exists.
 */
const buildChildEdges = (
  groupData: any,
  groupNodeId: string,
  serviceNodeId: string,
  msgIdToChildId: Map<string, string>,
  animateMessages: boolean,
): Edge[] => {
  const expandedEdges = groupData.expandedEdges || [];

  return (groupData.messages || [])
    .map((item: any) => {
      const msg = item.message;
      const msgId = `${msg.data.id}-${msg.data.version}`;
      const childId = msgIdToChildId.get(msgId)!;
      const collection = msg.collection || "events";
      const label = getChildEdgeLabel(groupData.direction, collection);

      if (groupData.direction === "sends") {
        return {
          id: `${serviceNodeId}-to-${childId}`,
          source: serviceNodeId,
          target: childId,
          label,
          type: "animated",
          animated: animateMessages,
          style: GROUP_EDGE_STYLE,
          markerEnd: GROUP_EDGE_MARKER,
        } as Edge;
      }

      // Receives: skip if a channel path already connects this message to the service
      if (canReachService(msgId, serviceNodeId, expandedEdges)) return null;

      return {
        id: `${childId}-to-${serviceNodeId}`,
        source: childId,
        target: serviceNodeId,
        label,
        type: "animated",
        animated: animateMessages,
        style: GROUP_EDGE_STYLE,
        markerEnd: GROUP_EDGE_MARKER,
      } as Edge;
    })
    .filter(Boolean) as Edge[];
};

const NodeGraphBuilder = ({
  nodes: initialNodes,
  edges: initialEdges,
  title,
  includeBackground = true,
  linkTo: _linkTo = "docs",
  includeKey = true,
  linksToVisualiser = false,
  links = [],
  mode = "full",
  showFlowWalkthrough = true,
  showSearch = true,
  zoomOnScroll = false,
  isStudioModalOpen,
  setIsStudioModalOpen = () => {},
  isChatEnabled = false,
  maxTextSize,
  isDevMode = false,
  resourceKey,
  animated,
  disableMessageAnimation = false,
  focusNodeId,
  focusRequestId,
  fitRequestId,
  onNodeClick,
  onBuildUrl,
  onNavigate,
  onSaveLayout,
  onResetLayout,
}: Props) => {
  // Wire up the URL builder so all nodes can use buildUrl() with the correct base path
  useEffect(() => {
    if (onBuildUrl) {
      setBuildUrlFn(onBuildUrl);
    }
  }, []);

  const nodeTypes = useMemo(() => {
    const wrapWithContextMenu = (Component: React.ComponentType<any>) => {
      const Wrapped = memo((props: any) => {
        const items = props.data?.contextMenu;
        if (!items?.length) return <Component {...props} />;
        return (
          <NodeContextMenu items={items}>
            <Component {...props} />
          </NodeContextMenu>
        );
      });
      Wrapped.displayName = `WithContextMenu(${Component.displayName || Component.name || "Component"})`;
      return Wrapped;
    };

    return {
      service: wrapWithContextMenu(ServiceNode),
      services: wrapWithContextMenu(ServiceNode),
      agent: wrapWithContextMenu(AgentNode),
      agents: wrapWithContextMenu(AgentNode),
      agentTool: wrapWithContextMenu(AgentToolNode),
      "agent-tool": wrapWithContextMenu(AgentToolNode),
      flow: wrapWithContextMenu(FlowNode),
      flows: wrapWithContextMenu(FlowNode),
      event: wrapWithContextMenu(EventNode),
      events: wrapWithContextMenu(EventNode),
      channel: wrapWithContextMenu(ChannelNode),
      channels: wrapWithContextMenu(ChannelNode),
      query: wrapWithContextMenu(QueryNode),
      queries: wrapWithContextMenu(QueryNode),
      command: wrapWithContextMenu(CommandNode),
      commands: wrapWithContextMenu(CommandNode),
      domain: wrapWithContextMenu(DomainNode),
      domains: wrapWithContextMenu(DomainNode),
      system: wrapWithContextMenu(SystemNode),
      systems: wrapWithContextMenu(SystemNode),
      step: StepNode,
      user: UserNode,
      custom: CustomNode,
      externalSystem: wrapWithContextMenu(ExternalSystemNode),
      "external-system": wrapWithContextMenu(ExternalSystemNode2),
      entity: wrapWithContextMenu(EntityNode),
      entities: wrapWithContextMenu(EntityNode),
      data: wrapWithContextMenu(DataNode),
      view: wrapWithContextMenu(ViewNode),
      actor: ActorNode,
      "context-actor": ContextActorNode,
      container: wrapWithContextMenu(DataNode),
      "data-product": wrapWithContextMenu(DataProductNode),
      "data-products": wrapWithContextMenu(DataProductNode),
      group: GroupNode,
      "system-group": SystemGroupNode,
      note: memo((props: any) => <NoteNode {...props} readOnly={true} />),
      field: wrapWithContextMenu(FieldNode),
      messageGroup: MessageGroupNode,
      messageGroupExpanded: MessageGroupExpandedNode,
      flowExpanded: FlowExpandedNode,
    } as unknown as NodeTypes;
  }, []);
  const edgeTypes = useMemo(
    () =>
      ({
        animated: AnimatedMessageEdge,
        multiline: MultilineEdgeLabel,
        "flow-edge": FlowEdge,
        default: LabelledDefaultEdge,
        smoothstep: LabelledSmoothStepEdge,
        step: LabelledStepEdge,
      }) as Record<string, any>,
    [],
  );
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { fitView, getNodes, getIntersectingNodes, getZoom, setCenter } =
    useReactFlow();
  const storeApi = useStoreApi();
  const previousGraphInputRef = useRef({
    nodes: initialNodes,
    edges: initialEdges,
  });

  // Sync when parent passes new nodes/edges (e.g. playground re-parse)
  useEffect(() => {
    const previousGraphInput = previousGraphInputRef.current;
    previousGraphInputRef.current = {
      nodes: initialNodes,
      edges: initialEdges,
    };

    // React Flow's fitView prop handles the initial viewport after measuring
    // the nodes. Avoid scheduling a second animated fit on mount.
    if (
      previousGraphInput.nodes === initialNodes &&
      previousGraphInput.edges === initialEdges
    ) {
      return;
    }

    setNodes(initialNodes);
    setEdges(initialEdges);
    // fitView after React Flow processes the new nodes
    requestAnimationFrame(() => {
      fitView({ duration: 300, padding: 0.2 });
    });
  }, [initialNodes, initialEdges, setNodes, setEdges, fitView]);

  const [animateMessages, setAnimateMessages] = useState(true);
  const [_activeStepIndex, _setActiveStepIndex] = useState<number | null>(null);
  const [_isFullscreen, _setIsFullscreen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareUrlCopySuccess, setShareUrlCopySuccess] = useState(false);
  const [isMermaidView, setIsMermaidView] = useState(false);
  const [showMinimap, setShowMinimap] = useState(false);
  const [hasLayoutChanges, setHasLayoutChanges] = useState(false);
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const initialPositionsRef = useRef<Record<string, { x: number; y: number }>>(
    {},
  );
  // const [isStudioModalOpen, setIsStudioModalOpen] = useState(false);
  const [focusModeOpen, setFocusModeOpen] = useState(false);
  const [focusedNodeId, setFocusedNodeId] = useState<string | null>(null);

  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const openNotesModal = useCallback(() => setIsNotesModalOpen(true), []);

  // Track user interaction (drag/pan/zoom) to pause expensive SVG animations.
  // Uses a ref + direct DOM manipulation to avoid React re-renders.
  const interactionCountRef = useRef(0);

  const startInteraction = useCallback(() => {
    interactionCountRef.current += 1;
    if (interactionCountRef.current === 1) {
      reactFlowWrapperRef.current?.classList.add("ec-interaction-active");
    }
  }, []);

  const endInteraction = useCallback(() => {
    interactionCountRef.current = Math.max(0, interactionCountRef.current - 1);
    if (interactionCountRef.current === 0) {
      reactFlowWrapperRef.current?.classList.remove("ec-interaction-active");
    }
  }, []);

  // Highlight source/target nodes when hovering an edge.
  // Uses direct DOM manipulation (like startInteraction) to avoid re-renders.
  const hoveredEdgeNodesRef = useRef<Element[]>([]);

  const handleEdgeMouseEnter = useCallback(
    (_: React.MouseEvent, edge: Edge) => {
      const wrapper = reactFlowWrapperRef.current;
      if (!wrapper) return;
      const nodes = wrapper.querySelectorAll(
        `[data-id="${edge.source}"], [data-id="${edge.target}"]`,
      );
      nodes.forEach((el) => el.classList.add("ec-edge-hover-node"));
      hoveredEdgeNodesRef.current = Array.from(nodes);
    },
    [],
  );

  const handleEdgeMouseLeave = useCallback(() => {
    hoveredEdgeNodesRef.current.forEach((el) =>
      el.classList.remove("ec-edge-hover-node"),
    );
    hoveredEdgeNodesRef.current = [];
  }, []);

  // Highlight all connected edges + their other-end nodes when hovering a node.
  // Looks up connected edges from the edges array, then queries DOM by edge ID.
  // Uses direct DOM manipulation to avoid re-renders.
  const hoveredNodeEdgesRef = useRef<Element[]>([]);
  const hoveredNodePeersRef = useRef<Element[]>([]);

  const handleNodeMouseEnter = useCallback(
    (_: React.MouseEvent, node: Node) => {
      const wrapper = reactFlowWrapperRef.current;
      if (!wrapper) return;

      const peerIds = new Set<string>();
      const edgeEls: Element[] = [];

      for (const edge of edgesRef.current) {
        if (edge.source !== node.id && edge.target !== node.id) continue;
        const el = wrapper.querySelector(
          `.react-flow__edge[data-id="${edge.id}"]`,
        );
        if (el) {
          el.classList.add("ec-node-hover-edge");
          edgeEls.push(el);
        }
        if (edge.source !== node.id) peerIds.add(edge.source);
        if (edge.target !== node.id) peerIds.add(edge.target);
      }
      hoveredNodeEdgesRef.current = edgeEls;

      // Highlight the hovered node + all peer nodes
      peerIds.add(node.id);
      const selector = Array.from(peerIds)
        .map((id) => `[data-id="${id}"]`)
        .join(", ");
      if (selector) {
        const peerEls = wrapper.querySelectorAll(selector);
        peerEls.forEach((el) => el.classList.add("ec-edge-hover-node"));
        hoveredNodePeersRef.current = Array.from(peerEls);
      }
    },
    [],
  );

  const handleNodeMouseLeave = useCallback(() => {
    hoveredNodeEdgesRef.current.forEach((el) =>
      el.classList.remove("ec-node-hover-edge"),
    );
    hoveredNodeEdgesRef.current = [];
    hoveredNodePeersRef.current.forEach((el) =>
      el.classList.remove("ec-edge-hover-node"),
    );
    hoveredNodePeersRef.current = [];
  }, []);

  // Check if there are channels to determine if we need the visualizer functionality
  const hasChannels = useMemo(
    () => initialNodes.some((node: any) => node.type === "channels"),
    [initialNodes],
  );
  const { hideChannels, toggleChannelsVisibility } = useChannelVisibility({
    nodes,
    edges,
    setNodes,
    setEdges,
    skipProcessing: !hasChannels,
  });
  const searchRef = useRef<VisualiserSearchRef>(null);
  const reactFlowWrapperRef = useRef<HTMLDivElement>(null);
  const scrollableContainerRef = useRef<HTMLElement | null>(null);

  // Stable refs — avoids recreating callbacks on every drag/state change
  const nodesRef = useRef(nodes);
  nodesRef.current = nodes;
  const edgesRef = useRef(edges);
  edgesRef.current = edges;
  const hideChannelsRef = useRef(hideChannels);
  hideChannelsRef.current = hideChannels;

  // The walkthrough treats expanded wrapper containers as structural rather
  // than walkable steps. Stitched predecessor/successor edges added at expand
  // time already connect real outer steps to the sub-flow's children, so
  // dropping wrapper edges doesn't break traversal.
  const wrapperNodeIds = useMemo(() => {
    const ids = new Set<string>();
    nodes.forEach((n) => {
      if (isExpandedWrapper(n.type)) ids.add(n.id);
    });
    return ids;
  }, [nodes]);
  const walkthroughNodes = useMemo(
    () =>
      wrapperNodeIds.size === 0
        ? nodes
        : nodes.filter((n) => !wrapperNodeIds.has(n.id)),
    [nodes, wrapperNodeIds],
  );
  const walkthroughEdges = useMemo(
    () =>
      wrapperNodeIds.size === 0
        ? edges
        : edges.filter(
            (e) =>
              !wrapperNodeIds.has(e.source) && !wrapperNodeIds.has(e.target),
          ),
    [edges, wrapperNodeIds],
  );

  const animateLayout = useCallback(() => {
    const wrapper = reactFlowWrapperRef.current;
    if (!wrapper) return;
    wrapper.classList.add("ec-animating-layout");
    setTimeout(() => wrapper.classList.remove("ec-animating-layout"), 400);
  }, []);

  const relayoutGraph = useCallback(
    (
      nextNodes: Node[],
      nextEdges: Edge[],
      anchor?: { id: string; position: { x: number; y: number } },
    ) => {
      const g = new dagre.graphlib.Graph({ compound: true });
      g.setGraph({ rankdir: "LR", ranksep: 300, nodesep: 50 });
      g.setDefaultEdgeLabel(() => ({}));

      // Add nodes to dagre — skip children (they're positioned relative to parent)
      nextNodes.forEach((node) => {
        if (node.parentId) return;
        const w =
          (node.style?.width as number) ||
          (node.type === "messageGroupExpanded" ? 380 : 150);
        const h =
          (node.style?.height as number) ||
          (node.type === "messageGroupExpanded" ? 0 : 120);

        // For expanded groups, calculate height from child count
        if (node.type === "messageGroupExpanded") {
          const children = nextNodes.filter((n) => n.parentId === node.id);
          const childHeight = children.length * 190 + 100; // 190px per child + padding
          g.setNode(node.id, { width: w, height: childHeight });
        } else {
          g.setNode(node.id, { width: w, height: h });
        }
      });

      // Add edges — only between top-level nodes or from top-level to parent of child
      nextEdges.forEach((edge) => {
        const sourceNode = nextNodes.find((n) => n.id === edge.source);
        const targetNode = nextNodes.find((n) => n.id === edge.target);
        const sourceTop = sourceNode?.parentId || edge.source;
        const targetTop = targetNode?.parentId || edge.target;
        // Only add edge if both endpoints are in the dagre graph
        if (
          g.hasNode(sourceTop) &&
          g.hasNode(targetTop) &&
          sourceTop !== targetTop
        ) {
          g.setEdge(sourceTop, targetTop);
        }
      });

      layoutDagreGraph(g);

      // Apply dagre positions to top-level nodes
      const positioned = nextNodes.map((node) => {
        if (node.parentId) {
          const parent = nextNodes.find((n) => n.id === node.parentId);

          // Sub-flow expansion: children keep the LR positions already computed
          // when the sub-flow was expanded. Leave them alone so the inner graph
          // renders in true graph order, not a vertical stack.
          if (parent?.type === "flowExpanded") {
            return node;
          }

          // Message groups: simple vertical stack, centred within the container.
          const parentWidth = (parent?.style?.width as number) || 380;
          const childWidth = 240; // initial estimate, refined by post-render measurement
          const xOffset = Math.max(20, (parentWidth - childWidth) / 2);
          const siblings = nextNodes.filter(
            (n) => n.parentId === node.parentId,
          );
          const index = siblings.indexOf(node);
          return {
            ...node,
            position: { x: xOffset, y: 70 + index * 190 },
          };
        }

        const pos = g.node(node.id);
        if (!pos) return node;
        // dagre returns center positions — convert to top-left for ReactFlow
        return {
          ...node,
          position: { x: pos.x - pos.width / 2, y: pos.y - pos.height / 2 },
        };
      });

      if (!anchor) return positioned;

      const positionedAnchor = positioned.find((node) => node.id === anchor.id);
      if (!positionedAnchor) return positioned;

      const offset = {
        x: anchor.position.x - positionedAnchor.position.x,
        y: anchor.position.y - positionedAnchor.position.y,
      };

      return positioned.map((node) => {
        if (node.parentId) return node;
        return {
          ...node,
          position: {
            x: node.position.x + offset.x,
            y: node.position.y + offset.y,
          },
        };
      });
    },
    [],
  );

  const makeRoomForRenderedExpandedGroup = useCallback(
    (
      groupNodeId: string,
      groupBounds: { x: number; y: number; width: number; height: number },
    ) => {
      const padding = 80;
      const protectedBounds = {
        x: groupBounds.x - padding,
        y: groupBounds.y - padding,
        width: groupBounds.width + padding * 2,
        height: groupBounds.height + padding * 2,
      };
      const intersectingIds = new Set(
        getIntersectingNodes(protectedBounds, true)
          .filter((node) => node.id !== groupNodeId && !node.parentId)
          .map((node) => node.id),
      );

      if (intersectingIds.size === 0) return;

      setNodes((currentNodes) => {
        const plannedPositions = packNodesAroundBounds({
          nodes: currentNodes,
          movableNodeIds: intersectingIds,
          protectedBounds,
          groupNodeId,
        });

        return currentNodes.map((node) => {
          const plannedPosition = plannedPositions.get(node.id);
          if (!plannedPosition) return node;
          return { ...node, position: plannedPosition };
        });
      });
    },
    [getIntersectingNodes, setNodes],
  );

  // Lay a sub-flow's internal nodes out with dagre LR and return positioned
  // children, a per-id position map, and the tightest container bounding box
  // so the wrapper can size itself to fit. `sizeOf` supplies per-child width
  // and height — callers pass either estimated constants (initial layout) or
  // measured DOM dimensions (post-paint).
  const layoutSubFlowChildren = useCallback(
    (
      children: Node[],
      edges: Edge[],
      sizeOf: (n: Node) => { w: number; h: number },
      opts: {
        padding: number;
        headerH: number;
        fallbackW?: number;
        fallbackH?: number;
      },
    ) => {
      const { padding, headerH, fallbackW = 240, fallbackH = 120 } = opts;

      const g = new dagre.graphlib.Graph();
      g.setGraph({ rankdir: "LR", ranksep: 360, nodesep: 200 });
      g.setDefaultEdgeLabel(() => ({}));
      const childIds = new Set(children.map((c) => c.id));
      children.forEach((c) => {
        const { w, h } = sizeOf(c);
        g.setNode(c.id, { width: w, height: h });
      });
      edges.forEach((e) => {
        if (childIds.has(e.source) && childIds.has(e.target)) {
          g.setEdge(e.source, e.target);
        }
      });
      layoutDagreGraph(g);

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;
      const positions = new Map<string, { x: number; y: number }>();
      children.forEach((c) => {
        const pos = g.node(c.id);
        if (!pos) return;
        const x = pos.x - pos.width / 2;
        const y = pos.y - pos.height / 2;
        positions.set(c.id, { x, y });
        minX = Math.min(minX, x);
        minY = Math.min(minY, y);
        maxX = Math.max(maxX, x + pos.width);
        maxY = Math.max(maxY, y + pos.height);
      });

      const offsetX = padding - (Number.isFinite(minX) ? minX : 0);
      const offsetY = headerH + padding - (Number.isFinite(minY) ? minY : 0);
      const positioned = children.map((c) => {
        const p = positions.get(c.id);
        if (!p) return c;
        return { ...c, position: { x: p.x + offsetX, y: p.y + offsetY } };
      });
      // Translate positions too so callers that read the map see the final coords.
      const finalPositions = new Map<string, { x: number; y: number }>();
      positions.forEach((p, id) =>
        finalPositions.set(id, { x: p.x + offsetX, y: p.y + offsetY }),
      );

      const width = Number.isFinite(minX)
        ? maxX - minX + padding * 2
        : fallbackW + padding * 2;
      const height = Number.isFinite(minY)
        ? maxY - minY + headerH + padding * 2
        : fallbackH + headerH + padding * 2;

      return { positioned, positions: finalPositions, width, height };
    },
    [],
  );

  // Read from refs inside the callback to avoid nodes/edges in the dependency array.
  // This prevents the callback from being recreated on every drag tick (Rule 2).
  const handleCollapseGroup = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      const collapseBtn = target.closest(
        ".ec-collapse-group-btn, .ec-collapse-flow-btn",
      );
      if (!collapseBtn) return;
      e.stopPropagation();

      // Find the ReactFlow node wrapper to get the node ID
      const nodeWrapper = target.closest("[data-id]");
      if (!nodeWrapper) return;
      const groupNodeId = nodeWrapper.getAttribute("data-id");
      if (!groupNodeId) return;

      const currentNodes = nodesRef.current;
      const currentEdges = edgesRef.current;

      // Find the expanded container node to get its data
      const expandedNode = currentNodes.find((n) => n.id === groupNodeId);
      if (!expandedNode || !isExpandedWrapper(expandedNode.type)) return;

      // The wrapper carries a pre-expansion snapshot — use it so nested
      // wrappers (created mid-session and therefore absent from initialNodes)
      // collapse correctly. Fall back to initialNodes for wrappers expanded
      // on a top-level node that was there from page load.
      const stashed = (expandedNode.data as any).__preExpansion as
        | {
            node: Node;
            edges: Edge[];
            nodeIds: string[];
            nodePositions?: Record<string, { x: number; y: number }>;
          }
        | undefined;
      const originalNode =
        stashed?.node ??
        initialNodes.find(
          (n: Node) =>
            n.id === groupNodeId &&
            (n.type === "messageGroup" ||
              n.type === "flows" ||
              n.type === "flow"),
        );
      if (!originalNode) return;
      const originalEdges =
        stashed?.edges ??
        initialEdges.filter(
          (edge: Edge) =>
            edge.source === groupNodeId || edge.target === groupNodeId,
        );

      const childNodeIds = new Set(
        currentNodes.filter((n) => n.parentId === groupNodeId).map((n) => n.id),
      );

      // Downstream nodes added on expand are those in the original node's
      // expandedNodes that weren't in the graph just before the expand ran.
      // Use the stashed pre-expansion id snapshot when present (so mid-
      // session wrappers diff against their own "before"), else fall back
      // to the initial page-load node set.
      const originalData = originalNode.data as any;
      const preExistingIds = new Set(
        stashed ? stashed.nodeIds : initialNodes.map((n: Node) => n.id),
      );
      const downstreamNodeIds = new Set(
        ((originalData.expandedNodes as any[]) || [])
          .map((n: any) => n.id)
          .filter((id: string) => !preExistingIds.has(id)),
      );

      const isDownstreamEdge = (edge: Edge) =>
        edge.id.startsWith(`${groupNodeId}__`);

      // Animate the layout transition
      animateLayout();

      // Swap: remove expanded container + children + downstream, restore compact node.
      // Only remove downstream nodes that aren't still referenced by surviving edges
      // (another expanded group may share the same channel/consumer nodes).
      setNodes((prev) => {
        // Compute which edges will survive the collapse
        const nextEdges = currentEdges
          .filter(
            (edge) =>
              !childNodeIds.has(edge.source) &&
              !childNodeIds.has(edge.target) &&
              !isDownstreamEdge(edge),
          )
          .concat(originalEdges);

        // Nodes still referenced by surviving edges must be kept
        const referencedByEdges = new Set<string>();
        for (const edge of nextEdges) {
          referencedByEdges.add(edge.source);
          referencedByEdges.add(edge.target);
        }

        const without = prev
          .filter(
            (n) =>
              n.id !== groupNodeId &&
              !childNodeIds.has(n.id) &&
              !(downstreamNodeIds.has(n.id) && !referencedByEdges.has(n.id)),
          )
          .map((n) => {
            const stashedPosition = stashed?.nodePositions?.[n.id];
            if (!stashedPosition || n.parentId) return n;
            return { ...n, position: stashedPosition };
          });
        const next = [
          ...without,
          {
            ...originalNode,
            position:
              stashed?.nodePositions?.[groupNodeId] ?? expandedNode.position,
          },
        ];
        return next;
      });
      setEdges((prev) => {
        const without = prev.filter(
          (edge) =>
            !childNodeIds.has(edge.source) &&
            !childNodeIds.has(edge.target) &&
            !isDownstreamEdge(edge),
        );
        return [...without, ...originalEdges];
      });
    },
    [
      initialNodes,
      initialEdges,
      setNodes,
      setEdges,
      relayoutGraph,
      animateLayout,
    ],
  );

  // Store initial node positions for change detection (dev mode only)
  useEffect(() => {
    if (isDevMode && initialNodes.length > 0) {
      const positions: Record<string, { x: number; y: number }> = {};
      initialNodes.forEach((node: Node) => {
        positions[node.id] = { x: node.position.x, y: node.position.y };
      });
      initialPositionsRef.current = positions;
    }
  }, [isDevMode, initialNodes]);

  // Detect layout changes by comparing current positions to initial positions
  const checkForLayoutChanges = useCallback(() => {
    if (!isDevMode) return;
    const initial = initialPositionsRef.current;
    if (Object.keys(initial).length === 0) return;

    const hasChanges = nodesRef.current.some((node) => {
      const initialPos = initial[node.id];
      return (
        initialPos &&
        (Math.abs(node.position.x - initialPos.x) > POSITION_CHANGE_THRESHOLD ||
          Math.abs(node.position.y - initialPos.y) > POSITION_CHANGE_THRESHOLD)
      );
    });

    setHasLayoutChanges(hasChanges);
  }, [isDevMode]);

  // Wrap onNodesChange to detect layout changes after node drag
  const handleNodesChange = useCallback(
    (changes: NodeChange[]) => {
      onNodesChange(changes);
      // Check for position changes after drag ends
      const hasDragEnd = changes.some(
        (change) => change.type === "position" && !change.dragging,
      );
      if (hasDragEnd) {
        // Use setTimeout to ensure state is updated
        setTimeout(checkForLayoutChanges, 0);
      }
    },
    [onNodesChange, checkForLayoutChanges],
  );

  const resetNodesAndEdges = useCallback(() => {
    setNodes((nds) =>
      nds.map((node) => {
        node.style = { ...node.style, opacity: 1 };
        return { ...node, animated: animateMessages, selected: false };
      }),
    );
    setEdges((eds) =>
      eds.map((edge) => {
        edge.style = { ...edge.style, opacity: 1 };
        edge.labelStyle = { ...edge.labelStyle, opacity: 1 };
        return {
          ...edge,
          data: { ...edge.data, opacity: 1, animated: animateMessages },
          animated: animateMessages,
        };
      }),
    );
  }, [setNodes, setEdges, animateMessages]);

  useEffect(() => {
    if (!focusNodeId) return;
    const targetNode = nodesRef.current.find((node) => node.id === focusNodeId);
    if (!targetNode) return;

    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        selected: node.id === focusNodeId,
      })),
    );

    requestAnimationFrame(() => {
      fitView({
        duration: 450,
        padding: 0.35,
        nodes: [targetNode],
      });
    });
  }, [focusNodeId, focusRequestId, fitView, setNodes]);

  useEffect(() => {
    if (fitRequestId == null) return;
    requestAnimationFrame(() => {
      fitView({ duration: 400, padding: 0.2 });
    });
  }, [fitRequestId, fitView]);

  const handleNodeClick = useCallback(
    (_: any, node: Node) => {
      // If custom onNodeClick is provided, use it
      if (onNodeClick) {
        onNodeClick(node);
        return;
      }

      // Disable focus mode for flow and entity visualizations — but allow
      // flow-type nodes that carry precomputed expandedNodes to continue
      // through to the inline expand handler below.
      const isFlow = edgesRef.current.some(
        (edge: Edge) => edge.type === "flow-edge",
      );
      const isEntityVisualizer = nodesRef.current.some(
        (n: Node) => n.type === "entities",
      );
      const isExpandableFlow =
        (node.type === "flows" || node.type === "flow") &&
        Array.isArray((node.data as any)?.expandedNodes) &&
        (node.data as any).expandedNodes.length > 0;
      if ((isFlow || isEntityVisualizer) && !isExpandableFlow) return;

      // Disable focus mode for domain and expanded group nodes
      if (node.type === "domain" || node.type === "domains") return;
      // System nodes navigate to their map on click (handled by the node itself),
      // so don't open the focus-mode modal for them.
      if (node.type === "system" || node.type === "systems") return;
      // Context-diagram actor nodes are non-interactive — clicking does nothing.
      if (node.type === "context-actor") return;
      // The system-group boundary box on the System Diagram is just a container.
      if (node.type === "system-group") return;
      if (isExpandedWrapper(node.type)) return;

      // Handle messageGroup click — expand inline
      if (node.type === "messageGroup") {
        const groupData = node.data as any;
        const groupNodeId = node.id;
        const currentGroupNode = getExpandedMessageGroupNode(
          nodesRef.current,
          groupNodeId,
        );
        if (currentGroupNode?.type === "messageGroupExpanded") {
          const measured = (currentGroupNode as any)?.measured;
          const width =
            measured?.width ??
            (currentGroupNode.style?.width as number | undefined) ??
            380;
          const height =
            measured?.height ??
            (currentGroupNode.style?.height as number | undefined) ??
            300;

          setCenter(
            currentGroupNode.position.x + width / 2,
            currentGroupNode.position.y + height / 2,
            {
              duration: 300,
              zoom: Math.min(Math.max(getZoom(), 0.55), 1),
            },
          );
          return;
        }

        const serviceNodeId = `${groupData.service.id}-${groupData.service.version}`;

        // Calculate expanded container dimensions based on message count
        const childCount = groupData.messages?.length || 0;
        const containerWidth = 380;
        const containerHeight = childCount * 190 + 100; // 190px per child + header + padding

        // Capture the node, its edges, and the set of node ids as they are
        // right now so collapse can (a) restore the pre-expansion node/edges
        // verbatim even for wrappers created mid-session, and (b) identify
        // which downstream nodes were newly introduced by this expansion.
        const preExpansionEdges = edgesRef.current.filter(
          (e) => e.source === groupNodeId || e.target === groupNodeId,
        );
        const preExpansionNodeIds = nodesRef.current.map((n) => n.id);
        const preExpansionNodePositions = Object.fromEntries(
          nodesRef.current.map((n) => [n.id, { ...n.position }]),
        );

        const expandedContainerNode: Node = {
          id: groupNodeId,
          type: "messageGroupExpanded",
          position: node.position,
          data: {
            groupName: groupData.groupName,
            direction: groupData.direction,
            messageCount: childCount,
            onCollapse: groupNodeId,
            __preExpansion: {
              node,
              edges: preExpansionEdges,
              nodeIds: preExpansionNodeIds,
              nodePositions: preExpansionNodePositions,
            },
          },
          style: {
            width: containerWidth,
            height: containerHeight,
          },
        };

        // Build child message nodes (operation fields are pre-computed server-side)
        const childNodes: Node[] = (groupData.messages || []).map(
          (item: any, index: number) => {
            const msg = item.message;
            const msgId = `${msg.data.id}-${msg.data.version}`;
            return {
              id: `${groupNodeId}__${msgId}`,
              type: msg.collection,
              parentId: groupNodeId,
              extent: "parent" as const,
              position: { x: 70, y: 70 + index * 190 },
              data: {
                mode: groupData.mode || "simple",
                message: { ...msg.data },
              },
            } as Node;
          },
        );

        // Map original message IDs → child node IDs (used for edge remapping)
        const msgIdToChildId = new Map<string, string>();
        for (const item of groupData.messages || []) {
          const msg = item.message;
          const msgId = `${msg.data.id}-${msg.data.version}`;
          msgIdToChildId.set(msgId, `${groupNodeId}__${msgId}`);
        }

        // Build service↔child edges.
        // For sends: always create service → child (we stripped this edge server-side).
        // For receives: only create child → service when no channel path already
        //   connects this message to the service through downstream edges.
        const childEdges = buildChildEdges(
          groupData,
          groupNodeId,
          serviceNodeId,
          msgIdToChildId,
          animateMessages,
        );

        // Downstream nodes/edges: pre-computed server-side (channels, consumers, producers).
        // Remap edge endpoints from original message IDs to child node IDs.
        // Deduplicate by ID — multiple grouped messages may share the same downstream path.
        // When channels are hidden, filter out channel nodes and their edges.
        const channelsHidden = hideChannelsRef.current;
        const channelNodeIds = channelsHidden
          ? new Set(
              (groupData.expandedNodes || [])
                .filter((n: any) => n.type === "channels")
                .map((n: any) => n.id),
            )
          : new Set<string>();

        const downstreamNodes: Node[] = (groupData.expandedNodes || []).filter(
          (n: any) => !channelNodeIds.has(n.id),
        );

        const seenEdgeIds = new Set<string>();
        const downstreamEdges: Edge[] = (groupData.expandedEdges || [])
          .map((e: any) => ({
            ...e,
            type: "animated",
            source: msgIdToChildId.get(e.source) || e.source,
            target: msgIdToChildId.get(e.target) || e.target,
            id: `${groupNodeId}__${e.id}`,
          }))
          .filter((e: Edge) => {
            if (seenEdgeIds.has(e.id)) return false;
            seenEdgeIds.add(e.id);
            // Drop edges that touch a hidden channel node
            if (channelNodeIds.has(e.source) || channelNodeIds.has(e.target))
              return false;
            return true;
          });

        // Animate the layout transition
        animateLayout();

        // Swap: remove compact node + its edge, add expanded container + children + downstream + edges
        setNodes((prev) => {
          const downstreamX =
            groupData.direction === "sends"
              ? node.position.x + containerWidth + 260
              : node.position.x - 420;
          const downstreamY = node.position.y + 40;
          return buildMessageGroupExpansionNodes({
            currentNodes: prev,
            groupNodeId,
            expandedContainerNode,
            childNodes,
            downstreamNodes,
            getDownstreamPosition: (_downstreamNode, index) => ({
              x: downstreamX,
              y: downstreamY + index * 190,
            }),
          }) as Node[];
        });
        setEdges((prev) => {
          const without = prev.filter(
            (e) => e.source !== groupNodeId && e.target !== groupNodeId,
          );
          return [...without, ...childEdges, ...downstreamEdges];
        });

        // After React renders the new nodes, measure actual sizes, resize container, and distribute evenly
        requestAnimationFrame(() => {
          let actualContainerBounds = {
            x: node.position.x,
            y: node.position.y,
            width: containerWidth,
            height: containerHeight,
          };

          setNodes((prev) => {
            const children = prev.filter((n) => n.parentId === groupNodeId);
            if (children.length === 0) return prev;

            // Measure actual rendered dimensions
            const measurements = children.map((n) => {
              const el = document.querySelector(
                `[data-id="${n.id}"]`,
              ) as HTMLElement | null;
              return {
                id: n.id,
                w: el?.offsetWidth ?? 240,
                h: el?.offsetHeight ?? 120,
              };
            });

            const padding = 30;
            const headerH = 50;
            const gap = 25;
            const totalChildH =
              measurements.reduce((sum, m) => sum + m.h, 0) +
              gap * (measurements.length - 1);
            const actualContainerH = headerH + totalChildH + padding * 2;

            let currentY = headerH + padding;
            actualContainerBounds = {
              x: node.position.x,
              y: node.position.y,
              width: containerWidth,
              height: actualContainerH,
            };

            return prev.map((n) => {
              // Resize the container to fit actual content
              if (n.id === groupNodeId) {
                actualContainerBounds = {
                  x: n.position.x,
                  y: n.position.y,
                  width: containerWidth,
                  height: actualContainerH,
                };
                return {
                  ...n,
                  style: {
                    ...n.style,
                    height: actualContainerH,
                  },
                };
              }
              if (n.parentId !== groupNodeId) return n;
              const m = measurements.find((m) => m.id === n.id);
              if (!m) return n;
              const x = Math.max(0, (containerWidth - m.w) / 2);
              const y = currentY;
              currentY += m.h + gap;
              return { ...n, position: { x, y } };
            });
          });

          requestAnimationFrame(() => {
            const groupNode = getNodes().find((n) => n.id === groupNodeId);
            const measured = (groupNode as any)?.measured;
            const width =
              measured?.width ??
              (groupNode?.style?.width as number | undefined) ??
              containerWidth;
            const height =
              measured?.height ??
              (groupNode?.style?.height as number | undefined) ??
              actualContainerBounds.height;
            const bounds = {
              x: groupNode?.position.x ?? actualContainerBounds.x,
              y: groupNode?.position.y ?? actualContainerBounds.y,
              width,
              height,
            };

            makeRoomForRenderedExpandedGroup(groupNodeId, bounds);
            setCenter(bounds.x + width / 2, bounds.y + height / 2, {
              duration: 450,
              zoom: Math.min(Math.max(getZoom(), 0.55), 1),
            });
          });
        });

        return;
      }

      // Handle flow-reference click — inline-expand the sub-flow
      if (isExpandableFlow) {
        const flowData = node.data as any;
        const flowNodeId = node.id;
        const subFlowNodes: any[] = flowData.expandedNodes || [];
        const subFlowEdges: any[] = flowData.expandedEdges || [];

        const initialChildNodes: Node[] = subFlowNodes.map((child) => ({
          ...child,
          parentId: flowNodeId,
          extent: "parent" as const,
          position: { x: 0, y: 0 },
        }));

        const CHILD_PADDING = 60;
        const HEADER_H = 60;
        const EST_W = 240;
        const EST_H = 120;
        const {
          positioned: childNodes,
          width: containerWidth,
          height: containerHeight,
        } = layoutSubFlowChildren(
          initialChildNodes,
          subFlowEdges as Edge[],
          () => ({ w: EST_W, h: EST_H }),
          { padding: CHILD_PADDING, headerH: HEADER_H },
        );

        // Capture the node, its edges, and the set of node ids as they are
        // right now so collapse can (a) restore the pre-expansion node/edges
        // verbatim even for wrappers created mid-session, and (b) identify
        // which downstream nodes were newly introduced by this expansion.
        const preExpansionEdges = edgesRef.current.filter(
          (e) => e.source === flowNodeId || e.target === flowNodeId,
        );
        const preExpansionNodeIds = nodesRef.current.map((n) => n.id);

        const expandedContainerNode: Node = {
          id: flowNodeId,
          type: "flowExpanded",
          position: node.position,
          data: {
            flowName: flowData.flow?.name || flowData.flow?.data?.name,
            version: flowData.flow?.version || flowData.flow?.data?.version,
            __preExpansion: {
              node,
              edges: preExpansionEdges,
              nodeIds: preExpansionNodeIds,
            },
          },
          style: {
            width: containerWidth,
            height: containerHeight,
            background: "transparent",
            border: "none",
            padding: 0,
            overflow: "visible",
          },
        };

        const childIds = new Set(childNodes.map((n) => n.id));
        const hasIncoming = new Set<string>();
        const hasOutgoing = new Set<string>();
        for (const e of subFlowEdges) {
          if (childIds.has(e.target)) hasIncoming.add(e.target);
          if (childIds.has(e.source)) hasOutgoing.add(e.source);
        }
        const entryChildIds = childNodes
          .map((n) => n.id)
          .filter((id) => !hasIncoming.has(id));
        const terminalChildIds = childNodes
          .map((n) => n.id)
          .filter((id) => !hasOutgoing.has(id));

        // Reroute the parent graph's edges across the sub-flow's boundary:
        // each predecessor now targets every entry child; each successor
        // sources from every terminal child.
        const currentEdges = edgesRef.current;
        const predecessorEdges = currentEdges.filter(
          (e: Edge) => e.target === flowNodeId,
        );
        const successorEdges = currentEdges.filter(
          (e: Edge) => e.source === flowNodeId,
        );

        const stitchedPredecessors: Edge[] = [];
        for (const e of predecessorEdges) {
          for (const entryId of entryChildIds.length > 0
            ? entryChildIds
            : [flowNodeId]) {
            stitchedPredecessors.push({
              ...e,
              id: `${e.id}__to__${entryId}`,
              target: entryId,
            });
          }
        }
        const stitchedSuccessors: Edge[] = [];
        for (const e of successorEdges) {
          for (const terminalId of terminalChildIds.length > 0
            ? terminalChildIds
            : [flowNodeId]) {
            stitchedSuccessors.push({
              ...e,
              id: `${e.id}__from__${terminalId}`,
              source: terminalId,
            });
          }
        }

        animateLayout();

        setNodes((prev) => {
          const without = prev.filter((n) => n.id !== flowNodeId);
          const next = [...without, expandedContainerNode, ...childNodes];
          const nextEdges = [
            ...currentEdges.filter(
              (e) => e.source !== flowNodeId && e.target !== flowNodeId,
            ),
            ...subFlowEdges,
            ...stitchedPredecessors,
            ...stitchedSuccessors,
          ];
          return relayoutGraph(next, nextEdges);
        });
        setEdges((prev) => {
          const without = prev.filter(
            (e) => e.source !== flowNodeId && e.target !== flowNodeId,
          );
          return [
            ...without,
            ...subFlowEdges,
            ...stitchedPredecessors,
            ...stitchedSuccessors,
          ];
        });

        // Once React has painted, re-run the LR layout with measured node
        // sizes so the container sizes itself to real content.
        requestAnimationFrame(() => {
          const currentChildren = nodesRef.current.filter(
            (n) => n.parentId === flowNodeId,
          );
          if (currentChildren.length === 0) return;

          const measurements = new Map<string, { w: number; h: number }>();
          currentChildren.forEach((n) => {
            const el = document.querySelector(
              `[data-id="${n.id}"]`,
            ) as HTMLElement | null;
            measurements.set(n.id, {
              w: el?.offsetWidth ?? EST_W,
              h: el?.offsetHeight ?? EST_H,
            });
          });

          const {
            positions,
            width: actualContainerW,
            height: actualContainerH,
          } = layoutSubFlowChildren(
            currentChildren,
            subFlowEdges as Edge[],
            (n) => measurements.get(n.id) ?? { w: EST_W, h: EST_H },
            { padding: CHILD_PADDING, headerH: HEADER_H },
          );

          setNodes((prev) =>
            prev.map((n) => {
              if (n.id === flowNodeId) {
                return {
                  ...n,
                  style: {
                    ...n.style,
                    width: actualContainerW,
                    height: actualContainerH,
                  },
                };
              }
              if (n.parentId !== flowNodeId) return n;
              const p = positions.get(n.id);
              if (!p) return n;
              return { ...n, position: { x: p.x, y: p.y } };
            }),
          );
        });

        requestAnimationFrame(() => {
          fitView({ duration: 400, padding: 0.2 });
        });

        return;
      }

      // Legacy behavior for linksToVisualiser (deprecated - use onNodeClick instead).
      // Keep this after inline expand handlers so embedded overview graphs can
      // still expand message groups/sub-flows without opening focus mode.
      if (linksToVisualiser && onNavigate) {
        return;
      }

      // Open focus mode modal
      setFocusedNodeId(node.id);
      setFocusModeOpen(true);
    },
    [
      onNodeClick,
      linksToVisualiser,
      onNavigate,
      makeRoomForRenderedExpandedGroup,
      getNodes,
      getZoom,
      setCenter,
    ],
  );

  const toggleAnimateMessages = useCallback(() => {
    setAnimateMessages((prev) => {
      const next = !prev;
      localStorage.setItem(
        "EventCatalog:animateMessages",
        JSON.stringify(next),
      );
      return next;
    });
  }, []);

  // Handle fit to view
  const handleFitView = useCallback(() => {
    fitView({ duration: 400, padding: 0.2 });
  }, [fitView]);

  // animate messages, between views
  // Priority: disableMessageAnimation > animated prop > URL parameter > localStorage > auto (disabled for large graphs)
  useEffect(() => {
    // Some graphs (e.g. the Context Diagram) aren't message flows, so animation
    // is unsupported — always off, ignoring every other source.
    if (disableMessageAnimation) {
      setAnimateMessages(false);
      return;
    }

    if (animated !== undefined) {
      setAnimateMessages(animated);
      return;
    }

    const urlParams = new URLSearchParams(window.location.search);
    const animateParam = urlParams.get("animate");

    if (animateParam === "true") {
      setAnimateMessages(true);
    } else if (animateParam === "false") {
      setAnimateMessages(false);
    } else {
      // Fall back to localStorage if no URL parameter
      const storedAnimateMessages = localStorage.getItem(
        "EventCatalog:animateMessages",
      );
      if (storedAnimateMessages !== null) {
        setAnimateMessages(storedAnimateMessages === "true");
      } else {
        // Auto-disable animation for large graphs to keep the canvas responsive
        setAnimateMessages(initialNodes.length <= LARGE_GRAPH_NODE_THRESHOLD);
      }
    }
  }, [animated, disableMessageAnimation, initialNodes.length]);

  useEffect(() => {
    setEdges((eds) =>
      eds.map((edge) => {
        const preservesOwnRenderer =
          edge.type === "flow-edge" ||
          edge.type === "multiline" ||
          edge.type === "default" ||
          edge.type === "step" ||
          edge.type === "smoothstep" ||
          edge.data?.animated === false;

        return {
          ...edge,
          animated: preservesOwnRenderer ? false : animateMessages,
          type: preservesOwnRenderer
            ? edge.type || "default"
            : animateMessages
              ? "animated"
              : "smoothstep",
          data: {
            ...edge.data,
            animateMessages: preservesOwnRenderer ? false : animateMessages,
            animated: preservesOwnRenderer ? false : animateMessages,
          },
        };
      }),
    );
  }, [animateMessages]);

  useEffect(() => {
    setTimeout(() => {
      fitView({ duration: 800 });
    }, 150);
  }, []);

  // Generate mermaid code lazily (only when needed for copy)
  const generateMermaidCode = useCallback(() => {
    try {
      return convertToMermaid(nodesRef.current, edgesRef.current, {
        includeStyles: true,
        direction: "LR",
      });
    } catch (error) {
      console.error("Error generating mermaid code:", error);
      return "";
    }
  }, []);

  // Handle scroll wheel events to forward to page when no modifier keys are pressed
  // Only when zoomOnScroll is disabled
  // This is a fix for when we embed node graphs into pages, and users are scrolling the documentation pages
  // We dont want REACT FLOW to swallow the scroll events, so we forward them to the parent page
  useEffect(() => {
    // Skip scroll handling if zoomOnScroll is enabled
    if (zoomOnScroll) return;

    // Cache the scrollable container on mount (expensive operation done once)
    const findScrollableContainer = (): HTMLElement | null => {
      // Try specific known selectors first (fast)
      const selectors = [
        ".docs-layout .overflow-y-auto",
        ".overflow-y-auto",
        '[style*="overflow-y:auto"]',
        '[style*="overflow-y: auto"]',
      ];

      for (const selector of selectors) {
        const element = document.querySelector(selector) as HTMLElement;
        if (element) return element;
      }

      return null;
    };

    // Find and cache the scrollable container once
    if (!scrollableContainerRef.current) {
      scrollableContainerRef.current = findScrollableContainer();
    }

    const handleWheel = (event: WheelEvent) => {
      // Only forward scroll if no modifier keys are pressed
      if (!event.ctrlKey && !event.shiftKey && !event.metaKey) {
        event.preventDefault();

        const scrollableContainer = scrollableContainerRef.current;

        if (scrollableContainer) {
          scrollableContainer.scrollBy({
            top: event.deltaY,
            left: event.deltaX,
            behavior: "instant",
          });
        } else {
          // Fallback to window scroll
          window.scrollBy({
            top: event.deltaY,
            left: event.deltaX,
            behavior: "instant",
          });
        }
      }
    };

    const wrapper = reactFlowWrapperRef.current;
    if (wrapper) {
      wrapper.addEventListener("wheel", handleWheel, { passive: false });
      return () => {
        wrapper.removeEventListener("wheel", handleWheel);
      };
    }
  }, [zoomOnScroll]);

  const handlePaneClick = useCallback(() => {
    searchRef.current?.hideSuggestions();
    resetNodesAndEdges();
    fitView({ duration: 800 });
  }, [resetNodesAndEdges, fitView]);

  const handleNodeSelect = useCallback(
    (node: Node) => {
      handleNodeClick(null, node);
    },
    [handleNodeClick],
  );

  const handleSearchClear = useCallback(() => {
    resetNodesAndEdges();
    fitView({ duration: 800 });
  }, [resetNodesAndEdges, fitView]);

  const downloadImage = useCallback((dataUrl: string, filename?: string) => {
    const a = document.createElement("a");
    a.setAttribute("download", `${filename || "eventcatalog"}.png`);
    a.setAttribute("href", dataUrl);
    a.click();
  }, []);

  const openStudioModal = useCallback(() => {
    setIsStudioModalOpen(true);
  }, [setIsStudioModalOpen]);

  const openChat = useCallback(() => {
    window.dispatchEvent(new CustomEvent("eventcatalog:open-chat"));
  }, []);

  // Layout persistence handlers (dev mode only)
  const handleSaveLayout = useCallback(async (): Promise<boolean> => {
    if (!resourceKey || !onSaveLayout) return false;

    const positions: Record<string, { x: number; y: number }> = {};
    nodesRef.current.forEach((node) => {
      positions[node.id] = {
        x: node.position.x,
        y: node.position.y,
      };
    });

    return await onSaveLayout(resourceKey, positions);
  }, [resourceKey, onSaveLayout]);

  const handleResetLayout = useCallback(async (): Promise<boolean> => {
    if (!resourceKey || !onResetLayout) return false;
    return await onResetLayout(resourceKey);
  }, [resourceKey, onResetLayout]);

  // Quick save handler for the change detection UI
  const handleQuickSaveLayout = useCallback(async () => {
    setIsSavingLayout(true);
    const success = await handleSaveLayout();
    setIsSavingLayout(false);
    if (success) {
      // Update initial positions to current positions after save
      const positions: Record<string, { x: number; y: number }> = {};
      nodesRef.current.forEach((node) => {
        positions[node.id] = { x: node.position.x, y: node.position.y };
      });
      initialPositionsRef.current = positions;
      setHasLayoutChanges(false);
    }
  }, [handleSaveLayout]);

  const handleCopyArchitectureCode = useCallback(async () => {
    const code = generateMermaidCode();
    await copyToClipboard(code);
  }, [generateMermaidCode]);

  const handleCopyShareUrl = useCallback(async () => {
    const url = typeof window !== "undefined" ? window.location.href : "";
    await copyToClipboard(url);
    setShareUrlCopySuccess(true);
    setTimeout(() => setShareUrlCopySuccess(false), 2000);
  }, []);

  const toggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      reactFlowWrapperRef.current?.requestFullscreen().catch((err) => {
        console.error(
          `Error attempting to enable full-screen mode: ${err.message} (${err.name})`,
        );
      });
    } else {
      document.exitFullscreen();
    }
  }, []);

  useEffect(() => {
    const handleFullscreenChange = () => {
      _setIsFullscreen(!!document.fullscreenElement);
      setTimeout(() => {
        fitView({ duration: 800 });
      }, 100);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, [fitView]);

  const handleExportVisual = useCallback(() => {
    // nodeLookup resolves child node positions (relative to their parent
    // group) to absolute coordinates — without it grouped graphs get cropped
    const nodesBounds = getNodesBounds(getNodes(), {
      nodeLookup: storeApi.getState().nodeLookup,
    });
    const { width, height, viewport } = getExportImageDimensions(nodesBounds);

    // Hide controls during export
    const controls = document.querySelector(
      ".react-flow__controls",
    ) as HTMLElement;
    if (controls) controls.style.display = "none";

    const viewportElement = document.querySelector(
      ".react-flow__viewport",
    ) as HTMLElement;
    const removeExportStyles = injectExportStyles(viewportElement);

    toPng(viewportElement, {
      backgroundColor: "#f1f1f1",
      width,
      height,
      style: {
        width: `${width}px`,
        height: `${height}px`,
        transform: `translate(${viewport.x}px, ${viewport.y}px) scale(${viewport.zoom})`,
      },
    })
      .then((dataUrl: string) => {
        downloadImage(dataUrl, title);
      })
      .finally(() => {
        removeExportStyles();
        if (controls) controls.style.display = "block";
      });
  }, [getNodes, storeApi, downloadImage, title]);

  const handleLegendClick = useCallback(
    (collectionType: string, groupId?: string) => {
      const isLegendTarget = (node: Node<any>) => {
        if (groupId) {
          return node.data.group && node.data.group?.id === groupId;
        }
        return node.type === collectionType;
      };

      const updatedNodes = nodes.map((node: Node<any>) => {
        if (isLegendTarget(node)) {
          return { ...node, style: { ...node.style, opacity: 1 } };
        }
        return { ...node, style: { ...node.style, opacity: 0.1 } };
      });

      const updatedEdges = edges.map((edge) => {
        return {
          ...edge,
          data: { ...edge.data, opacity: 0.1 },
          style: { ...edge.style, opacity: 0.1 },
          labelStyle: { ...edge.labelStyle, opacity: 0.1 },
          animated: animateMessages,
        };
      });

      setNodes(updatedNodes);
      setEdges(updatedEdges);

      const targetNodes = updatedNodes.filter(isLegendTarget);
      if (targetNodes.length === 0) return;

      fitView({
        padding: 0.2,
        duration: 800,
        nodes: targetNodes,
      });
    },
    [nodes, edges, setNodes, setEdges, fitView],
  );

  const getNodesByCollectionWithColors = useCallback((nodes: Node<any>[]) => {
    const colorClasses = {
      events: "bg-orange-600",
      agent: "bg-sky-600",
      agents: "bg-sky-600",
      agentTool: "bg-violet-600",
      "agent-tool": "bg-violet-600",
      services: "bg-pink-600",
      flows: "bg-teal-600",
      commands: "bg-blue-600",
      queries: "bg-green-600",
      channels: "bg-gray-600",
      externalSystem: "bg-pink-600",
      systems: "bg-purple-600",
      system: "bg-purple-600",
      actor: "bg-yellow-500",
      "context-actor": "bg-yellow-500",
      step: "bg-gray-700",
      data: "bg-blue-600",
      "data-products": "bg-indigo-600",
      field: "bg-cyan-600",
      messageGroup: "bg-violet-600",
      messageGroupExpanded: "bg-violet-600",
    };

    let legendForDomains: {
      [key: string]: { count: number; colorClass: string; groupId: string };
    } = {};

    // Find any groups
    const domainGroups = [
      ...new Set(
        nodes
          .filter(
            (node) => node.data.group && node.data.group?.type === "Domain",
          )
          .map((node) => node.data.group?.id),
      ),
    ];

    domainGroups.forEach((groupId) => {
      const group = nodes.filter(
        (node) => node.data.group && node.data.group?.id === groupId,
      );
      legendForDomains[`${groupId} (Domain)`] = {
        count: group.length,
        colorClass: "bg-yellow-600",
        groupId,
      };
    });

    const legendForNodes = nodes.reduce(
      (
        acc: {
          [key: string]: {
            count: number;
            colorClass: string;
            groupId?: string;
          };
        },
        node,
      ) => {
        const collection = node.type;
        if (collection) {
          if (acc[collection]) {
            acc[collection].count += 1;
          } else {
            acc[collection] = {
              count: 1,
              colorClass:
                colorClasses[collection as keyof typeof colorClasses] ||
                "bg-black",
            };
          }
        }
        return acc;
      },
      {},
    );

    return { ...legendForDomains, ...legendForNodes };
  }, []);

  // Legend only depends on node types and groups, not positions.
  // Use a ref to avoid recomputing the key string on every drag tick.
  const legendKeyRef = useRef("");
  const computedLegendKey = nodes
    .map((n: Node<any>) => `${n.id}:${n.type}:${n.data.group?.id || ""}`)
    .join(",");
  if (computedLegendKey !== legendKeyRef.current) {
    legendKeyRef.current = computedLegendKey;
  }
  const legendKey = legendKeyRef.current;

  const legend = useMemo(
    () => getNodesByCollectionWithColors(nodes),
    [getNodesByCollectionWithColors, legendKey],
  );

  // Stable key derived from node IDs — only changes when nodes are added/removed,
  // not when positions change during drag. Used by search, legend, and notes.
  const nodeIdsKeyRef = useRef("");
  const computedNodeIdsKey = nodes.map((n) => n.id).join(",");
  if (computedNodeIdsKey !== nodeIdsKeyRef.current) {
    nodeIdsKeyRef.current = computedNodeIdsKey;
  }
  const nodeIdsKey = nodeIdsKeyRef.current;

  const searchNodes = useMemo(() => nodes, [nodeIdsKey]);

  // Collect notes across all nodes for the dropdown menu item.
  // Notes don't change during drag — use nodeIdsKey for stability.
  const allNoteGroups = useMemo(() => {
    const groups: {
      nodeId: string;
      name: string;
      notes: any[];
      nodeType: string;
    }[] = [];
    for (const node of nodes) {
      const result = getNotesFromNode(node);
      if (result) {
        groups.push({
          nodeId: node.id,
          name: result.name,
          notes: result.notes,
          nodeType: result.nodeType,
        });
      }
    }
    return groups;
  }, [nodeIdsKey]);

  const totalNotesCount = useMemo(
    () => allNoteGroups.reduce((sum, g) => sum + g.notes.length, 0),
    [allNoteGroups],
  );

  const handleStepChange = useCallback(
    (
      nodeId: string | null,
      highlightPaths?: string[],
      shouldZoomOut?: boolean,
    ) => {
      if (nodeId === null) {
        // Reset all nodes and edges
        resetNodesAndEdges();
        _setActiveStepIndex(null);

        // If shouldZoomOut is true, fit the entire view
        if (shouldZoomOut) {
          setTimeout(() => {
            fitView({ duration: 800, padding: 0.1 });
          }, 100);
        }
        return;
      }

      const activeNode = nodes.find((node: Node) => node.id === nodeId);
      if (!activeNode) return;

      // Create set of highlighted nodes and edges
      const highlightedNodeIds = new Set<string>();
      const highlightedEdgeIds = new Set<string>();

      // Add current node
      highlightedNodeIds.add(activeNode.id);

      // Add incoming edges and their source nodes
      edges.forEach((edge: Edge) => {
        if (edge.target === activeNode.id) {
          highlightedEdgeIds.add(edge.id);
          highlightedNodeIds.add(edge.source);
        }
      });

      // Add outgoing edges
      if (highlightPaths) {
        // Highlight all possible paths when at a fork
        highlightPaths.forEach((pathId) => {
          const [source, target] = pathId.split("-");
          edges.forEach((edge: Edge) => {
            if (edge.source === source && edge.target === target) {
              highlightedEdgeIds.add(edge.id);
              highlightedNodeIds.add(edge.target);
            }
          });
        });
      } else {
        // Highlight all outgoing edges normally
        edges.forEach((edge: Edge) => {
          if (edge.source === activeNode.id) {
            highlightedEdgeIds.add(edge.id);
            highlightedNodeIds.add(edge.target);
          }
        });
      }

      // Update nodes
      const updatedNodes = nodes.map((node: Node) => {
        if (highlightedNodeIds.has(node.id)) {
          return { ...node, style: { ...node.style, opacity: 1 } };
        }
        return { ...node, style: { ...node.style, opacity: 0.2 } };
      });

      // Update edges
      const updatedEdges = edges.map((edge: Edge) => {
        if (highlightedEdgeIds.has(edge.id)) {
          return {
            ...edge,
            data: { ...edge.data, opacity: 1, animated: true },
            style: { ...edge.style, opacity: 1, strokeWidth: 3 },
            labelStyle: { ...edge.labelStyle, opacity: 1 },
            animated: true,
          };
        }
        return {
          ...edge,
          data: { ...edge.data, opacity: 0.2, animated: false },
          style: { ...edge.style, opacity: 0.2, strokeWidth: 2 },
          labelStyle: { ...edge.labelStyle, opacity: 0.2 },
          animated: false,
        };
      });

      setNodes(updatedNodes);
      setEdges(updatedEdges);

      // Fit view to active node
      fitView({
        padding: 0.4,
        duration: 800,
        nodes: [activeNode],
      });
    },
    [nodes, edges, setNodes, setEdges, resetNodesAndEdges, fitView],
  );

  // Check if this is a flow visualization by checking if edges use flow-edge type
  const isFlowVisualization = useMemo(
    () => edges.some((edge: Edge) => edge.type === "flow-edge"),
    [edges],
  );
  const isCompactMenuButton = !title;
  const menuButtonClassName = isCompactMenuButton
    ? "h-9 w-9 p-0 bg-[rgb(var(--ec-card-bg))] hover:bg-[rgb(var(--ec-accent-subtle))] border border-[rgb(var(--ec-page-border))] rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ec-accent))] flex items-center justify-center transition-colors duration-150 hover:border-[rgb(var(--ec-accent)/0.3)] group"
    : "py-2.5 px-4 bg-[rgb(var(--ec-card-bg))] hover:bg-[rgb(var(--ec-accent-subtle))] border border-[rgb(var(--ec-page-border))] rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ec-accent))] flex items-center gap-3 transition-colors duration-150 hover:border-[rgb(var(--ec-accent)/0.3)] group whitespace-nowrap";
  const menuIconClassName = isCompactMenuButton
    ? "h-4 w-4 text-[rgb(var(--ec-page-text-muted))] flex-shrink-0 group-hover:text-[rgb(var(--ec-accent))] transition-colors duration-150"
    : "h-5 w-5 text-[rgb(var(--ec-page-text-muted))] flex-shrink-0 group-hover:text-[rgb(var(--ec-accent))] transition-colors duration-150";

  return (
    <div
      ref={reactFlowWrapperRef}
      onClick={handleCollapseGroup}
      className="w-full h-full bg-[rgb(var(--ec-page-bg))] flex flex-col eventcatalog-visualizer"
      style={{
        width: "100%",
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <PortalContainerProvider value={reactFlowWrapperRef.current}>
        {isMermaidView ? (
          <>
            {/* Menu Bar for Mermaid View */}
            <div className="w-full pr-6 flex space-x-2 justify-between items-center bg-[rgb(var(--ec-page-bg))] border-b border-[rgb(var(--ec-page-border))] p-4">
              <div className="flex space-x-2 ml-4">
                {/* Settings Dropdown Menu */}
                <DropdownMenu.Root>
                  <DropdownMenu.Trigger asChild>
                    <button
                      className={menuButtonClassName}
                      aria-label="Open menu"
                    >
                      {title && (
                        <span className="text-base font-medium text-[rgb(var(--ec-page-text))] leading-tight">
                          {title}
                        </span>
                      )}
                      <MoreVertical className={menuIconClassName} />
                    </button>
                  </DropdownMenu.Trigger>
                  <DropdownMenu.Portal container={reactFlowWrapperRef.current}>
                    <DropdownMenu.Content
                      className="min-w-56 bg-[rgb(var(--ec-page-bg))] border border-[rgb(var(--ec-page-border))] rounded-lg shadow-xl z-50 py-1.5 animate-in fade-in zoom-in-95 duration-200"
                      sideOffset={0}
                      align="end"
                      alignOffset={-180}
                    >
                      <DropdownMenu.Arrow className="fill-[rgb(var(--ec-page-bg))] stroke-[rgb(var(--ec-page-border))] stroke-1" />
                      <VisualizerDropdownContent
                        isMermaidView={isMermaidView}
                        setIsMermaidView={setIsMermaidView}
                        animateMessages={animateMessages}
                        toggleAnimateMessages={toggleAnimateMessages}
                        hideAnimateMessages={disableMessageAnimation}
                        hideChannels={hideChannels}
                        toggleChannelsVisibility={toggleChannelsVisibility}
                        hasChannels={hasChannels}
                        showMinimap={showMinimap}
                        setShowMinimap={setShowMinimap}
                        handleFitView={handleFitView}
                        searchRef={searchRef}
                        isChatEnabled={isChatEnabled}
                        openChat={openChat}
                        handleCopyArchitectureCode={handleCopyArchitectureCode}
                        handleExportVisual={handleExportVisual}
                        setIsShareModalOpen={setIsShareModalOpen}
                        toggleFullScreen={toggleFullScreen}
                        openStudioModal={openStudioModal}
                        isDevMode={isDevMode}
                        onSaveLayout={handleSaveLayout}
                        onResetLayout={handleResetLayout}
                      />
                    </DropdownMenu.Content>
                  </DropdownMenu.Portal>
                </DropdownMenu.Root>
              </div>
              {mode === "full" && showSearch && (
                <div className="flex justify-end items-center gap-2">
                  {!isMermaidView && (
                    <div className="w-96">
                      <VisualiserSearch
                        ref={searchRef}
                        nodes={searchNodes}
                        onNodeSelect={handleNodeSelect}
                        onClear={handleSearchClear}
                      />
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Mermaid View */}
            <div className="flex-1 overflow-hidden relative">
              <MermaidView
                nodes={nodes}
                edges={edges}
                maxTextSize={maxTextSize}
              />
            </div>
          </>
        ) : (
          <ReactFlow
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            minZoom={0.07}
            nodes={nodes}
            edges={edges}
            fitView
            fitViewOptions={INITIAL_FIT_VIEW_OPTIONS}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onEdgeMouseEnter={handleEdgeMouseEnter}
            onEdgeMouseLeave={handleEdgeMouseLeave}
            connectionLineType={ConnectionLineType.SmoothStep}
            nodeOrigin={NODE_ORIGIN}
            onNodeClick={handleNodeClick}
            onNodeMouseEnter={handleNodeMouseEnter}
            onNodeMouseLeave={handleNodeMouseLeave}
            onPaneClick={handlePaneClick}
            onMoveStart={startInteraction}
            onMoveEnd={endInteraction}
            onNodeDragStart={startInteraction}
            onNodeDragStop={endInteraction}
            zoomOnScroll={zoomOnScroll}
            className="relative"
          >
            <Panel
              position="top-center"
              className="w-full pr-6 pointer-events-none"
            >
              <div className="flex space-x-2 justify-between items-center pointer-events-auto">
                <div className="flex space-x-2 ml-4">
                  {/* Settings Dropdown Menu */}
                  <DropdownMenu.Root>
                    <DropdownMenu.Trigger asChild>
                      <button
                        className={menuButtonClassName}
                        aria-label="Open menu"
                      >
                        {title && (
                          <span className="text-base font-medium text-[rgb(var(--ec-page-text))] leading-tight">
                            {title}
                          </span>
                        )}
                        <MoreVertical className={menuIconClassName} />
                      </button>
                    </DropdownMenu.Trigger>
                    <DropdownMenu.Portal
                      container={reactFlowWrapperRef.current}
                    >
                      <DropdownMenu.Content
                        className="min-w-56 bg-[rgb(var(--ec-page-bg))] border border-[rgb(var(--ec-page-border))] rounded-lg shadow-xl z-50 py-1.5 animate-in fade-in zoom-in-95 duration-200"
                        sideOffset={0}
                        align="end"
                        alignOffset={-180}
                      >
                        <DropdownMenu.Arrow className="fill-[rgb(var(--ec-page-bg))] stroke-[rgb(var(--ec-page-border))] stroke-1" />
                        <VisualizerDropdownContent
                          isMermaidView={isMermaidView}
                          setIsMermaidView={setIsMermaidView}
                          animateMessages={animateMessages}
                          toggleAnimateMessages={toggleAnimateMessages}
                          hideAnimateMessages={disableMessageAnimation}
                          hideChannels={hideChannels}
                          toggleChannelsVisibility={toggleChannelsVisibility}
                          hasChannels={hasChannels}
                          showMinimap={showMinimap}
                          setShowMinimap={setShowMinimap}
                          handleFitView={handleFitView}
                          searchRef={searchRef}
                          isChatEnabled={isChatEnabled}
                          openChat={openChat}
                          handleCopyArchitectureCode={
                            handleCopyArchitectureCode
                          }
                          handleExportVisual={handleExportVisual}
                          setIsShareModalOpen={setIsShareModalOpen}
                          toggleFullScreen={toggleFullScreen}
                          openStudioModal={openStudioModal}
                          isDevMode={isDevMode}
                          onSaveLayout={handleSaveLayout}
                          onResetLayout={handleResetLayout}
                          notesCount={totalNotesCount}
                          onOpenNotes={openNotesModal}
                        />
                      </DropdownMenu.Content>
                    </DropdownMenu.Portal>
                  </DropdownMenu.Root>
                </div>
                {mode === "full" && showSearch && (
                  <div className="flex justify-end items-center gap-2">
                    {!isMermaidView && (
                      <div className="w-96">
                        <VisualiserSearch
                          ref={searchRef}
                          nodes={searchNodes}
                          onNodeSelect={handleNodeSelect}
                          onClear={handleSearchClear}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
              {links.length > 0 && (
                <div className="flex justify-end mt-3">
                  <div className="relative flex items-center -mt-1">
                    <span className="absolute left-2 pointer-events-none flex items-center h-full">
                      <HistoryIcon className="h-4 w-4 text-[rgb(var(--ec-page-text-muted))]" />
                    </span>
                    <select
                      value={
                        links.find((link) =>
                          window.location.href.includes(link.url),
                        )?.url || links[0].url
                      }
                      onChange={(e) => {
                        if (onNavigate) {
                          onNavigate(e.target.value);
                        } else {
                          window.location.href = e.target.value;
                        }
                      }}
                      className="appearance-none pl-7 pr-6 py-0 text-[14px] bg-[rgb(var(--ec-card-bg))] text-[rgb(var(--ec-page-text))] rounded-md border border-[rgb(var(--ec-page-border))] hover:bg-[rgb(var(--ec-page-border)/0.5)] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(var(--ec-accent))]"
                      style={{ minWidth: 120, height: "26px" }}
                    >
                      {links.map((link) => (
                        <option key={link.url} value={link.url}>
                          {link.label}
                        </option>
                      ))}
                    </select>
                    <span className="absolute right-2 pointer-events-none">
                      <svg
                        className="w-4 h-4 text-[rgb(var(--ec-page-text-muted))]"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </span>
                  </div>
                </div>
              )}
            </Panel>

            {includeBackground && (
              <Background color="var(--ec-bg-dots)" gap={16} />
            )}
            {includeBackground && <Controls />}
            {showMinimap && (
              <MiniMap
                nodeStrokeWidth={3}
                zoomable
                pannable
                style={MINIMAP_STYLE}
              />
            )}
            {isFlowVisualization && showFlowWalkthrough && (
              <Panel position="bottom-left">
                <StepWalkthrough
                  nodes={walkthroughNodes}
                  edges={walkthroughEdges}
                  isFlowVisualization={isFlowVisualization}
                  onStepChange={handleStepChange}
                  mode={mode}
                />
              </Panel>
            )}
            {/* Dev Mode: Layout change indicator */}
            {isDevMode && hasLayoutChanges && (
              <Panel
                position="bottom-left"
                style={
                  isFlowVisualization && showFlowWalkthrough
                    ? LAYOUT_CHANGE_PANEL_STYLE_WITH_WALKTHROUGH
                    : LAYOUT_CHANGE_PANEL_STYLE_DEFAULT
                }
              >
                <div className="bg-[rgb(var(--ec-card-bg))] border border-[rgb(var(--ec-page-border))] rounded-lg shadow-md px-3 py-2 flex items-center gap-3">
                  <span className="text-xs text-[rgb(var(--ec-page-text-muted))]">
                    Layout changed
                  </span>
                  <button
                    onClick={handleQuickSaveLayout}
                    disabled={isSavingLayout}
                    className="text-xs font-medium text-white bg-[rgb(var(--ec-accent))] hover:bg-[rgb(var(--ec-accent-hover))] px-2 py-1 rounded transition-colors disabled:opacity-50"
                  >
                    {isSavingLayout ? "Saving..." : "Save"}
                  </button>
                </div>
              </Panel>
            )}
            {includeKey && (
              <LegendPanel
                legend={legend}
                showMinimap={showMinimap}
                onLegendClick={handleLegendClick}
              />
            )}
          </ReactFlow>
        )}
        <StudioModal
          isOpen={isStudioModalOpen || false}
          onClose={() => setIsStudioModalOpen(false)}
        />
        <FocusModeModal
          isOpen={focusModeOpen}
          onClose={() => {
            setFocusModeOpen(false);
          }}
          initialNodeId={focusedNodeId}
          nodes={nodes}
          edges={edges}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
        />
        <AllNotesModal
          noteGroups={allNoteGroups}
          isOpen={isNotesModalOpen}
          onClose={() => setIsNotesModalOpen(false)}
          nodes={nodes}
        />

        {/* Share Link Modal */}
        {isShareModalOpen && (
          <>
            <div
              className="fixed inset-0 bg-black/20 z-40"
              onClick={() => setIsShareModalOpen(false)}
              style={{ animation: "fadeIn 150ms ease-out" }}
            />
            <div
              className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-[rgb(var(--ec-page-bg))] rounded-lg shadow-xl z-50 w-full max-w-md p-6 border border-[rgb(var(--ec-page-border))]"
              style={{ animation: "slideInCenter 250ms ease-out" }}
            >
              <style>{`
              @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
              }
              @keyframes slideInCenter {
                from { opacity: 0; transform: translate(-50%, -48%); }
                to { opacity: 1; transform: translate(-50%, -50%); }
              }
            `}</style>

              <div className="flex justify-between items-start mb-4">
                <h3 className="text-lg font-semibold text-[rgb(var(--ec-page-text))]">
                  Share Link
                </h3>
                <button
                  onClick={() => setIsShareModalOpen(false)}
                  className="text-[rgb(var(--ec-page-text-muted))] hover:text-[rgb(var(--ec-page-text))] transition-colors"
                  aria-label="Close modal"
                >
                  <ExternalLink className="w-5 h-5 rotate-180" />
                </button>
              </div>

              <p className="text-sm text-[rgb(var(--ec-page-text-muted))] mb-4">
                Share this link with your team to let them view this
                visualization.
              </p>

              <div className="flex gap-2">
                <input
                  type="text"
                  readOnly
                  value={
                    typeof window !== "undefined" ? window.location.href : ""
                  }
                  className="flex-1 px-3 py-2.5 bg-[rgb(var(--ec-input-bg))] border border-[rgb(var(--ec-input-border))] rounded-md text-[rgb(var(--ec-input-text))] text-sm focus:outline-none focus:ring-2 focus:ring-[rgb(var(--ec-accent))]"
                />
                <button
                  onClick={handleCopyShareUrl}
                  className={`px-4 py-2.5 rounded-md font-medium transition-all duration-200 flex items-center gap-2 ${
                    shareUrlCopySuccess
                      ? "bg-green-500 text-white"
                      : "bg-[rgb(var(--ec-accent))] text-white hover:opacity-90"
                  }`}
                  aria-label={shareUrlCopySuccess ? "Copied!" : "Copy link"}
                >
                  {shareUrlCopySuccess ? (
                    <CheckIcon className="w-4 h-4" />
                  ) : (
                    <ClipboardIcon className="w-4 h-4" />
                  )}
                  <span>{shareUrlCopySuccess ? "Copied!" : "Copy"}</span>
                </button>
              </div>
            </div>
          </>
        )}
      </PortalContainerProvider>
    </div>
  );
};

interface NodeGraphProps {
  id: string;
  title?: string;
  href?: string;
  hrefLabel?: string;
  nodes?: Node[];
  edges?: Edge[];
  graph?: DslGraph;
  linkTo?: "docs" | "visualiser";
  includeKey?: boolean;
  footerLabel?: string;
  linksToVisualiser?: boolean;
  links?: { label: string; url: string }[];
  mode?: "full" | "simple";
  portalId?: string;
  showFlowWalkthrough?: boolean;
  showSearch?: boolean;
  zoomOnScroll?: boolean;
  designId?: string;
  isChatEnabled?: boolean;
  maxTextSize?: number;
  isDevMode?: boolean;
  resourceKey?: string;
  /** Controls whether message flow animation is enabled. When set, overrides URL params and localStorage. */
  animated?: boolean;
  /**
   * When true, message-flow animation is unsupported for this graph: it is forced
   * off (ignoring the `animated` prop, URL params and localStorage) and the
   * "Simulate Messages" toggle is hidden. Used by graphs that aren't message
   * flows, e.g. the System Diagram.
   */
  disableMessageAnimation?: boolean;
  /** When set, the graph will zoom to this node id. */
  focusNodeId?: string;
  /** Optional token to force repeated focus for the same node id. */
  focusRequestId?: number;
  /** Optional token to trigger fit-to-screen behavior. */
  fitRequestId?: number;

  // Callback API for framework integration
  onNodeClick?: (node: Node) => void;
  onBuildUrl?: (path: string) => string;
  onNavigate?: (url: string) => void;
  onSaveLayout?: (
    resourceKey: string,
    positions: Record<string, { x: number; y: number }>,
  ) => Promise<boolean>;
  onResetLayout?: (resourceKey: string) => Promise<boolean>;
}

const NodeGraph = ({
  id,
  nodes: nodesProp,
  edges: edgesProp,
  graph,
  title: titleProp,
  href,
  linkTo = "docs",
  hrefLabel = "Open in visualizer",
  includeKey: includeKeyProp,
  footerLabel,
  linksToVisualiser = false,
  links = [],
  mode = "full",
  portalId,
  showFlowWalkthrough = true,
  showSearch: showSearchProp,
  zoomOnScroll = false,
  designId,
  isChatEnabled = false,
  maxTextSize,
  isDevMode = false,
  resourceKey,
  animated: animatedProp,
  focusNodeId,
  focusRequestId,
  fitRequestId,
  onNodeClick,
  onBuildUrl,
  onNavigate,
  onSaveLayout,
  onResetLayout,
}: NodeGraphProps) => {
  // When a DslGraph is provided, run layout internally using dagre.
  const graphLayout = useMemo(() => {
    if (!graph) return null;
    return layoutGraph(
      graph.nodes,
      graph.edges,
      { rankdir: "LR", nodesep: 60, ranksep: 120 },
      graph.options?.style,
    );
  }, [graph]);
  const nodes = graphLayout?.nodes ?? nodesProp ?? [];
  const edges = graphLayout?.edges ?? edgesProp ?? [];

  // Derive props from graph.options when graph is provided
  const title = titleProp ?? graph?.title;
  const includeKey =
    includeKeyProp !== undefined
      ? includeKeyProp
      : graph?.options?.legend !== false;
  const showSearch =
    showSearchProp !== undefined
      ? showSearchProp
      : graph?.options?.search !== false;
  const animated = animatedProp ?? graph?.options?.animated;
  const [elem, setElem] = useState(null);
  const [showFooter, setShowFooter] = useState(true);
  const [isStudioModalOpen, setIsStudioModalOpen] = useState(false);

  const openStudioModal = useCallback(() => {
    setIsStudioModalOpen(true);
  }, []);

  const containerToRenderInto = portalId || `${id}-portal`;

  useEffect(() => {
    // @ts-ignore
    setElem(document.getElementById(containerToRenderInto));
  }, []);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const embed = urlParams.get("embed");
    if (embed === "true") {
      setShowFooter(false);
    }
  }, []);

  if (!elem) return null;

  return (
    <div>
      {createPortal(
        <ReactFlowProvider>
          <NodeGraphBuilder
            edges={edges}
            nodes={nodes}
            title={title}
            linkTo={linkTo}
            includeKey={includeKey}
            linksToVisualiser={linksToVisualiser}
            links={links}
            mode={mode}
            showFlowWalkthrough={showFlowWalkthrough}
            showSearch={showSearch}
            zoomOnScroll={zoomOnScroll}
            designId={designId || id}
            isStudioModalOpen={isStudioModalOpen}
            setIsStudioModalOpen={setIsStudioModalOpen}
            isChatEnabled={isChatEnabled}
            maxTextSize={maxTextSize}
            isDevMode={isDevMode}
            resourceKey={resourceKey}
            animated={animated}
            focusNodeId={focusNodeId}
            focusRequestId={focusRequestId}
            fitRequestId={fitRequestId}
            onNodeClick={onNodeClick}
            onBuildUrl={onBuildUrl}
            onNavigate={onNavigate}
            onSaveLayout={onSaveLayout}
            onResetLayout={onResetLayout}
          />

          {showFooter && (
            <div className="flex justify-between" id="visualiser-footer">
              {footerLabel && (
                <div className="py-2 w-full text-left ">
                  <span className=" text-sm no-underline py-2 text-[rgb(var(--ec-page-text-muted))]">
                    {footerLabel}
                  </span>
                </div>
              )}

              {href && (
                <div className="py-2 w-full text-right flex justify-between">
                  {/* Studio link temporarily hidden */}
                  <span />
                  <a
                    className=" text-sm underline text-[rgb(var(--ec-page-text))] hover:text-[rgb(var(--ec-accent))]"
                    href={href}
                  >
                    {hrefLabel} &rarr;
                  </a>
                </div>
              )}
            </div>
          )}
        </ReactFlowProvider>,
        elem,
      )}
    </div>
  );
};

export type { NodeGraphProps };
export default NodeGraph;
